import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';
import { ErrorBoundary } from '@sentry/react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useRouter } from 'react-resource-router';

import { useAnalyticsWebClient } from '../../../utils/analytics/context';

export const AppWithRoute = ({ children }: { children: ReactNode }) => {
	const isInitial = useRef(true);
	// Consider using location.state if you would like to pass additional data while routing to skip the interaction.
	const [{ route }] = useRouter();
	const analyticsWebClient = useAnalyticsWebClient();

	if (route?.analyticsName) {
		/*
		 * Start instrumenting the page load event.
		 * We invoke `traceUFOPageLoad` once more
		 * when we have the route context.
		 * It attaches the route name to the event.
		 * */
		if (isInitial.current) {
			traceUFOPageLoad(route.analyticsName, route.name);
			isInitial.current = false;
		} else {
			traceUFOTransition(route.analyticsName, route.name);
		}
	}
	useEffect(() => {
		if (route?.analyticsName) {
			analyticsWebClient.sendOperationalEvent({
				action: 'rendered',
				actionSubject: `app.${route.analyticsName}`,
				source: 'app',
			});
		}
	}, [route?.analyticsName, analyticsWebClient]);

	return (
		<ErrorBoundary
			onError={(err) => {
				analyticsWebClient.sendOperationalEvent({
					action: 'error',
					actionSubject: `app.${route.analyticsName}`,
					source: 'app',
					attributes: {
						message: err?.message,
					},
				});
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
